"use client";

import { cn } from "@/lib/utils";
import type React from "react";
import type { ReactNode } from "react";
import { useMemo, memo } from "react";
interface FocusedAuroraBackgroundProps extends React.HTMLProps<HTMLDivElement> {
  children: ReactNode;
  beamIntensity?: "none" | "vlow" | "low" | "medium" | "high";
}
const INTENSITY_VALUES = {
  none: 0,
  vlow: 0.2,
  low: 0.6,
  medium: 0.8,
  high: 1
};
const FocusedAuroraBackgroundBase = ({
  className,
  children,
  beamIntensity = "medium",
  ...props
}: FocusedAuroraBackgroundProps) => {
  // Memoize values to prevent recalculations on each render
  const intensityValue = INTENSITY_VALUES[beamIntensity];

  // Memoize beam styles to prevent rebuilding them on each render
  const beamStyles = useMemo(() => {
    return {
      mainBeam: {
        background: `linear-gradient(to bottom, 
          rgba(249, 115, 22, 0) 0%, 
          rgba(249, 115, 22, ${intensityValue * 0.3}) 10%, 
          rgba(252, 211, 77, ${intensityValue * 0.7}) 50%, 
          rgba(249, 115, 22, ${intensityValue * 0.3}) 90%, 
          rgba(249, 115, 22, 0) 100%)`,
        filter: "blur(15px)"
      },
      centerBeam: {
        background: `linear-gradient(to bottom, 
          rgba(252, 211, 77, 0) 0%, 
          rgba(252, 211, 77, ${intensityValue * 0.5}) 10%, 
          rgba(254, 243, 199, ${intensityValue}) 50%, 
          rgba(252, 211, 77, ${intensityValue * 0.5}) 90%, 
          rgba(252, 211, 77, 0) 100%)`,
        filter: "blur(5px)"
      },
      radialOverlay: {
        background: "radial-gradient(circle at center, transparent 0%, rgba(0, 0, 0, 0.8) 70%)",
        mixBlendMode: "multiply" as const
      },
      topVignette: {
        background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0) 100%)"
      },
      bottomVignette: {
        background: "linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0) 100%)"
      },
      dotPattern: {
        backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px)`,
        backgroundSize: "20px 20px"
      }
    };
  }, [intensityValue]);

  // Use GPU acceleration and reduce repaints
  const containerClass = useMemo(() => {
    return cn("relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-3xl bg-zinc-950 text-slate-50 transform-gpu will-change-transform", className);
  }, [className]);
  return <div className={containerClass} {...props} data-sentry-component="FocusedAuroraBackgroundBase" data-sentry-source-file="focused-aurora-background.tsx">
      {/* Dot pattern background - static, doesn't need frequent updates */}
      <div className="translate-z-0 pointer-events-none absolute inset-0 z-0 opacity-30" style={beamStyles.dotPattern}></div>

      {/* Vertical beam container */}
      <div className="absolute inset-0 z-0 overflow-hidden contain-paint">
        {/* Main vertical beam - hardware accelerated */}
        <div className="translate-z-0 absolute bottom-0 left-1/2 top-0 w-[120px] -translate-x-1/2 opacity-70" style={beamStyles.mainBeam}></div>

        {/* Brighter center beam - hardware accelerated */}
        <div className="translate-z-0 absolute bottom-0 left-1/2 top-0 w-[40px] -translate-x-1/2 opacity-90" style={beamStyles.centerBeam}></div>

        {/* Radial overlay - static, doesn't need frequent updates */}
        <div className="translate-z-0 absolute inset-0" style={beamStyles.radialOverlay}></div>
      </div>

      {/* Dust particles - separate stacking context with hardware acceleration */}
      <div className="pointer-events-none absolute inset-0 z-0 contain-paint">
        {/* Static dust particles - these are defined in CSS to avoid creating too many DOM elements */}
        <div className="dust-particles"></div>
      </div>

      {/* Top vignette - static */}
      <div className="translate-z-0 pointer-events-none absolute left-0 right-0 top-0 z-0 h-[100px]" style={beamStyles.topVignette}></div>

      {/* Bottom vignette - static */}
      <div className="translate-z-0 pointer-events-none absolute bottom-0 left-0 right-0 z-0 h-[100px]" style={beamStyles.bottomVignette}></div>

      {/* Content container with proper z-index */}
      <div className="relative z-10 flex h-full w-full flex-col">
        {children}
      </div>

      {/* Add CSS for dust particles animation with optimized keyframes */}
      <style jsx global>{`
        .dust-particles {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .dust-particles::before {
          content: "";
          position: absolute;
          width: 100%;
          height: 100%;
          background-image:
            radial-gradient(
              circle at 48% 10%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 52% 15%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 49% 25%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 51% 30%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 50% 40%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 48% 50%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 52% 60%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 49% 70%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 51% 75%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 50% 85%,
              rgba(255, 255, 255, 0.7) 0.075rem,
              transparent 0.1125rem
            ),
            radial-gradient(
              circle at 49.5% 20%,
              rgba(255, 255, 255, 0.8) 0.1125rem,
              transparent 0.15rem
            ),
            radial-gradient(
              circle at 50.5% 45%,
              rgba(255, 255, 255, 0.8) 0.1125rem,
              transparent 0.15rem
            ),
            radial-gradient(
              circle at 49.5% 65%,
              rgba(255, 255, 255, 0.8) 0.1125rem,
              transparent 0.15rem
            );
          animation: float 20s infinite ease-in-out;
          will-change: transform, opacity;
          transform: translateZ(0);
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0) translateX(0);
            opacity: 0.2;
          }
          25% {
            transform: translateY(-10px) translateX(5px);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-15px) translateX(-5px);
            opacity: 1;
          }
          75% {
            transform: translateY(-5px) translateX(10px);
            opacity: 0.5;
          }
        }
      `}</style>
    </div>;
};

// Wrap with memo to prevent unnecessary re-renders
const FocusedAuroraBackground = memo(FocusedAuroraBackgroundBase);
FocusedAuroraBackground.displayName = "FocusedAuroraBackground";
export { FocusedAuroraBackground };